import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, MapPin } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
export function Home() {
  useScreenInit();
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  };
  return <div className="w-full" data-id="element-887">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden" data-id="element-888">
        <div className="absolute inset-0 z-0" data-id="element-889">
          <img src="https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Club Interior" className="w-full h-full object-cover" data-id="element-890" />
          <div className="absolute inset-0 bg-club-navy/40 mix-blend-multiply" data-id="element-891"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-club-canvas via-transparent to-transparent opacity-90" data-id="element-892"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-20" data-id="element-893">
          <motion.span className="eyebrow text-club-gold mb-6 block" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5,
          duration: 1
        }} data-id="element-894">
            Est. 1999 · Chennai
          </motion.span>
          <motion.h1 className="text-4xl md:text-6xl font-serif text-white mb-6 leading-tight" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 1
        }} data-id="element-895">
            Andhra Social &amp; Cultural Association
          </motion.h1>
          <motion.p className="text-lg md:text-xl text-club-canvas/90 mb-10 font-light max-w-2xl mx-auto" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8,
          duration: 1
        }} data-id="element-896">
            A distinguished home for the Andhra community in Chennai — where
            Telugu heritage, culture, and camaraderie come together.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1,
          duration: 0.8
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-897">
            <Link to="/about" className="btn-gold" data-id="element-898">
              Discover Our Legacy
            </Link>
            <Link to="/contact" className="btn-secondary text-white border-white hover:bg-white/10" data-id="element-899">
              Membership Enquiries
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Heritage Intro */}
      <section className="py-24 px-4 bg-club-canvas" data-id="element-900">
        <div className="max-w-7xl mx-auto" data-id="element-901">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-902">
            <motion.div className="order-2 md:order-1" initial="initial" whileInView="animate" viewport={{
            once: true
          }} variants={fadeIn} data-id="element-903">
              <span className="eyebrow block mb-4" data-id="element-904">Our Story</span>
              <h2 className="text-4xl md:text-5xl mb-6" data-id="element-905">
                A Legacy of Culture and Community
              </h2>
              <p className="text-club-ink/70 mb-6 leading-relaxed" data-id="element-906">
                Founded in 1999, the Andhra Social & Cultural Association has
                stood as a beacon of heritage and camaraderie in the heart of
                Chennai. For generations, we have provided our members with an
                unparalleled sanctuary that celebrates the rich traditions of
                the Andhra community.
              </p>
              <p className="text-club-ink/70 mb-8 leading-relaxed" data-id="element-907">
                Our commitment to excellence is reflected in every detail, from
                our meticulously curated dining experiences to our world-class
                sporting facilities and luxurious accommodations.
              </p>
              <Link to="/about" className="inline-flex items-center text-club-gold font-medium hover:text-club-green transition-colors group" data-id="element-908">
                Read our full history
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-909" />
              </Link>
            </motion.div>
            <motion.div className="order-1 md:order-2 relative" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-910">
              <div className="aspect-[4/5] relative overflow-hidden rounded-sm" data-id="element-911">
                <img src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80" alt="Heritage Architecture" className="w-full h-full object-cover" data-id="element-912" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-club-green p-6 flex items-center justify-center text-center rounded-sm hidden md:flex" data-id="element-913">
                <p className="font-serif text-club-gold text-xl italic" data-id="element-914">
                  "A home away from home for the distinguished."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Facilities */}
      <section className="py-24 px-4 bg-club-canvasAlt border-y border-club-border" data-id="element-915">
        <div className="max-w-7xl mx-auto" data-id="element-916">
          <div className="text-center mb-16" data-id="element-917">
            <span className="eyebrow block mb-4" data-id="element-918">Exceptional Amenities</span>
            <h2 className="text-4xl md:text-5xl" data-id="element-919">World-Class Facilities</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-920">
            {[{
            title: 'Fine Dining',
            image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            desc: 'Experience culinary excellence across our three distinct restaurants and two private bars.',
            link: '/dining'
          }, {
            title: 'Sports & Wellness',
            image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            desc: 'State-of-the-art fitness center, indoor pool, squash courts, and bespoke spa treatments.',
            link: '/sports'
          }, {
            title: 'Luxury Accommodation',
            image: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
            desc: 'Twenty-four elegantly appointed suites offering comfort and privacy for members and guests.',
            link: '/accommodation'
          }].map((facility, index) => <motion.div key={facility.title} className="group cursor-pointer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} data-id="element-921">
                <div className="aspect-[4/3] overflow-hidden rounded-sm mb-6 relative" data-id="element-922">
                  <img src={facility.image} alt={facility.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-923" />
                  <div className="absolute inset-0 bg-club-navy/10 group-hover:bg-transparent transition-colors duration-500" data-id="element-924"></div>
                </div>
                <h3 className="text-2xl mb-3" data-id="element-925">{facility.title}</h3>
                <p className="text-club-ink/70 mb-4" data-id="element-926">{facility.desc}</p>
                <Link to={facility.link} className="inline-flex items-center text-sm font-medium text-club-green group-hover:text-club-gold transition-colors" data-id="element-927">
                  Explore <ArrowRight size={14} className="ml-1" data-id="element-928" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Upcoming Events Teaser */}
      <section className="py-24 px-4 bg-club-canvas" data-id="element-929">
        <div className="max-w-7xl mx-auto" data-id="element-930">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-931">
            <div data-id="element-932">
              <span className="eyebrow block mb-4" data-id="element-933">Social Calendar</span>
              <h2 className="text-4xl md:text-5xl" data-id="element-934">Upcoming Events</h2>
            </div>
            <Link to="/events" className="hidden md:inline-flex items-center text-club-gold font-medium hover:text-club-green transition-colors" data-id="element-935">
              View full calendar <ArrowRight size={16} className="ml-2" data-id="element-936" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-937">
            {/* Featured Event */}
            <motion.div className="relative aspect-square lg:aspect-auto lg:h-full rounded-sm overflow-hidden group" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-938">
              <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80" alt="Annual Gala" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-939" />
              <div className="absolute inset-0 bg-gradient-to-t from-club-navy/90 via-club-navy/40 to-transparent" data-id="element-940"></div>
              <div className="absolute bottom-0 left-0 p-8 w-full" data-id="element-941">
                <div className="flex items-center gap-4 text-club-gold mb-3 text-sm font-medium" data-id="element-942">
                  <span className="flex items-center" data-id="element-943">
                    <Calendar size={14} className="mr-1" data-id="element-944" /> Oct 15, 2026
                  </span>
                  <span className="flex items-center" data-id="element-945">
                    <Clock size={14} className="mr-1" data-id="element-946" /> 19:00
                  </span>
                </div>
                <h3 className="text-3xl text-white mb-2" data-id="element-947">
                  The Centenary Autumn Gala
                </h3>
                <p className="text-club-canvas/80 mb-4 max-w-md" data-id="element-948">
                  Join us for our most prestigious event of the year, featuring
                  a five-course tasting menu and live jazz.
                </p>
                <Link to="/events/gala" className="btn-primary py-2 px-4 text-sm" data-id="element-949">
                  Reserve Tickets
                </Link>
              </div>
            </motion.div>

            {/* Event List */}
            <div className="space-y-6" data-id="element-950">
              {[{
              date: 'Oct 22',
              title: 'Wine Tasting: Bordeaux Masterclass',
              time: '18:30',
              loc: 'The Cellar'
            }, {
              date: 'Nov 05',
              title: 'Members Autumn Golf Tournament',
              time: '08:00',
              loc: 'Championship Course'
            }, {
              date: 'Nov 12',
              title: 'Guest Speaker: Sir David Attenborough',
              time: '19:00',
              loc: 'Grand Ballroom'
            }, {
              date: 'Nov 28',
              title: 'Karthika Masam Special Dinner',
              time: '18:00',
              loc: 'The Godavari Dining Room'
            }].map((event, i) => <motion.div key={i} className="flex gap-6 p-6 border border-club-border rounded-sm hover:border-club-gold transition-colors bg-white group cursor-pointer" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              duration: 0.5
            }} data-id="element-951">
                  <div className="flex-shrink-0 text-center w-16" data-id="element-952">
                    <span className="block text-sm text-club-gold font-semibold uppercase" data-id="element-953">
                      {event.date.split(' ')[0]}
                    </span>
                    <span className="block text-2xl font-serif text-club-green" data-id="element-954">
                      {event.date.split(' ')[1]}
                    </span>
                  </div>
                  <div className="flex-grow" data-id="element-955">
                    <h4 className="text-xl mb-2 group-hover:text-club-gold transition-colors" data-id="element-956">
                      {event.title}
                    </h4>
                    <div className="flex items-center gap-4 text-sm text-club-ink/60" data-id="element-957">
                      <span className="flex items-center" data-id="element-958">
                        <Clock size={14} className="mr-1" data-id="element-959" /> {event.time}
                      </span>
                      <span className="flex items-center" data-id="element-960">
                        <MapPin size={14} className="mr-1" data-id="element-961" /> {event.loc}
                      </span>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
          <div className="mt-8 text-center md:hidden" data-id="element-962">
            <Link to="/events" className="btn-secondary w-full" data-id="element-963">
              View full calendar
            </Link>
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-24 px-4 bg-club-green text-club-canvas relative overflow-hidden" data-id="element-964">
        <div className="absolute inset-0 opacity-10" data-id="element-965">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-966">
            <defs data-id="element-967">
              <pattern id="pattern" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-968">
                <path d="M0 40L40 0H20L0 20M40 40V20L20 40" fill="currentColor" fillOpacity="0.5" data-id="element-969" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#pattern)" data-id="element-970" />
          </svg>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-971">
          <span className="eyebrow text-club-gold mb-6 block" data-id="element-972">
            Join The Community
          </span>
          <h2 className="text-4xl md:text-5xl mb-8 text-white" data-id="element-973">
            Membership Enquiries
          </h2>
          <p className="text-lg text-club-canvas/80 mb-10 font-light leading-relaxed" data-id="element-974">
            Membership at Andhra Social & Cultural Association is by invitation
            or through proposal by existing members. We welcome enquiries from
            individuals who share our values and appreciation for Telugu culture
            and excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-975">
            <Link to="/contact" className="btn-gold" data-id="element-976">
              Request Information
            </Link>
            <Link to="/about" className="btn-secondary text-white border-white hover:bg-white/10" data-id="element-977">
              Membership Criteria
            </Link>
          </div>
        </div>
      </section>
    </div>;
}